import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { NotificationStatus, NotificationType } from "@fitness-app/data-models/entities/Notification";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import { supabase } from "~/store/initializeStore";
import { sentryErrorTrackingService } from "~/store/sentryErrorTrackingService";

// Unread badges for the support board (TRE-1547): source of truth are the unread
// `notification` rows targeted at me (assigned / mention / deadline reminders), keyed
// by resourceId = requestId. Opening a request's drawer marks its rows as read, which
// clears the card badge, the menu badge AND the bell entry in one go (the bell's
// Realtime subscription picks up the UPDATE).
export const SUPPORT_UNREAD_QUERY_KEY = "support_unread_notifications";

const SUPPORT_NOTIFICATION_TYPES = [
  NotificationType.SupportRequestAssigned,
  NotificationType.SupportCommentMention,
  NotificationType.SupportRequestDueSoon,
  NotificationType.SupportRequestOverdue,
];

export const useSupportUnreadNotifications = (enabled = true) => {
  const { userId } = useUserRole();
  const queryClient = useQueryClient();
  // Unique per mount — the hook is used by the menu and the board at the same time,
  // and two Realtime channels must not share a topic name.
  const channelSuffix = useRef(Math.random().toString(36).slice(2));

  const { data } = useQuery({
    // Keyed by user — a switched account must not see the previous user's badge set.
    // Invalidations use the bare prefix, which matches this key too.
    queryKey: [SUPPORT_UNREAD_QUERY_KEY, userId],
    enabled: Boolean(userId) && enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notification")
        .select("id, resourceId")
        .eq("ownerId", userId!)
        .eq("status", NotificationStatus.UNREAD)
        .in("type", SUPPORT_NOTIFICATION_TYPES)
        // OOM guard only — badges may undercount past 500 unread support notifications,
        // which on an internal board means something is broken anyway.
        .limit(500);

      if (error) {
        throw error;
      }

      return data || [];
    },
  });

  useEffect(() => {
    if (!userId || !enabled) {
      return;
    }

    const channel = supabase
      .channel(`support-unread-${userId}-${channelSuffix.current}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notification", filter: `ownerId=eq.${userId}` },
        () => {
          void queryClient.invalidateQueries({ queryKey: [SUPPORT_UNREAD_QUERY_KEY] });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId, enabled, queryClient]);

  const unreadRequestIds = useMemo(
    () => new Set((data || []).map((row) => row.resourceId).filter((id): id is string => Boolean(id))),
    [data],
  );

  const markRequestNotificationsAsRead = useCallback(
    async (requestId: string) => {
      if (!userId) {
        return;
      }

      const { error } = await supabase
        .from("notification")
        .update({ status: NotificationStatus.READ, updatedAt: new Date().toISOString() })
        .eq("ownerId", userId)
        .eq("resourceId", requestId)
        .eq("status", NotificationStatus.UNREAD)
        .in("type", SUPPORT_NOTIFICATION_TYPES);

      if (error) {
        // Badge staleness self-heals on the next Realtime event / refetch, but the
        // failure must not be invisible.
        sentryErrorTrackingService.recordError(error, "supportUnreadMarkAsRead");
        return;
      }

      void queryClient.invalidateQueries({ queryKey: [SUPPORT_UNREAD_QUERY_KEY] });
    },
    [userId, queryClient],
  );

  return {
    unreadRequestIds,
    // Badge counts requests with news, not raw notification rows.
    unreadCount: unreadRequestIds.size,
    markRequestNotificationsAsRead,
  };
};
